import moment from 'moment';
import { validateYupSchema, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import { Client, ClientSubscription, ClientSubscriptionService, planTypes } from '../../core/_models';
import { Provider } from '../../../providers/core/_models';
import { ProviderService, isFeeService } from '../../../services/core/_models';

// WST-891 — the service-plan form model. Pure: no React components; formik is
// imported only for its Yup validation helpers. Unit-tested alone.

/**
 * The top-level tier the user picks first. 'prepaid' and 'postpaid' are the two
 * BILLING DIRECTIONS, each with its own list of kinds; 'manual' is a third tier
 * that is not a direction at all — it has no kinds under it (KINDS_BY_DIRECTION
 * .manual is empty), selects the `manual` plan type on its own, and renders no
 * kind radios. The type keeps its name because every caller pairs it with a
 * kind, and a manual plan pairs with the `manual` kind.
 */
export type PlanDirection = 'prepaid' | 'postpaid' | 'manual';
export type PlanKind = 'fixed' | 'schedule' | 'attendance' | 'tuition' | 'manual';
export type PlanTypeKey = keyof typeof planTypes;
export type BillingInterval = 'day' | 'week' | 'month';
export type BillingCadence = { interval: BillingInterval; count: number };

export type ServiceRowValue = {
  uuid?: string | null;
  provider_service?: ProviderService;
  rate?: number | null;
  payout?: number | null;
  service_quantity: number;
  service_interval_count: number;
  fee_frequency?: 'one_time' | 'recurring' | null; // WST-681: fee rows only
};

export type PlanFormValues = {
  provider?: Provider;
  client?: Client;
  currency?: string;
  /**
   * Unset until the user picks one. A CREATE form opens on no plan type at all
   * (WST-891 feedback: a seeded Prepaid/Fixed read as a decision already made),
   * and the kind radios do not render until a direction exists. Edit mode always
   * carries both — a stored plan has a plan_type — and locks them.
   */
  direction?: PlanDirection;
  kind?: PlanKind;
  start_date?: Date;
  end_date?: Date | null;
  period_rate?: number; // tuition only
  services: ServiceRowValue[];
  billing: BillingCadence;
  itemize_invoice: boolean;
  notify_on_payment: boolean;
  note?: string; // invite note (create mode)
  provider_note?: string | null;
};

/**
 * Kinds offered per direction, in display order (ticket §5). By Schedule leads
 * both lists: it is the kind the clinic is steered towards, and the one a fresh
 * direction preselects (see `preferredKind`).
 *
 * Manual is a TIER, not a kind: it holds no kinds at all, so picking it renders
 * nothing underneath. Everything that walks these lists therefore has to treat
 * the manual tier specially — see `visibleDirections`.
 */
export const KINDS_BY_DIRECTION: Record<PlanDirection, PlanKind[]> = {
  prepaid: ['schedule', 'fixed', 'tuition'],
  postpaid: ['schedule', 'attendance', 'fixed', 'tuition'],
  manual: [],
};

/**
 * The kind a freshly picked direction lands on when it is on offer. Only the
 * SELECT marks it "(Recommended)" — KIND_LABELS stays the plain wording the
 * plan card and the billing report print.
 */
export const RECOMMENDED_KIND: PlanKind = 'schedule';

/** The recommended kind if this direction offers it, else its first one. */
export const preferredKind = (kinds: PlanKind[]): PlanKind | undefined =>
  kinds.includes(RECOMMENDED_KIND) ? RECOMMENDED_KIND : kinds[0];

export const KIND_LABELS: Record<PlanKind, string> = {
  fixed: 'Fixed',
  schedule: 'By Schedule',
  attendance: 'By Attendance',
  tuition: 'Tuition',
  manual: 'Manual',
};

/** The ticket's one-line descriptions, keyed by direction+kind. */
export const KIND_DESCRIPTIONS: Record<PlanDirection, Partial<Record<PlanKind, string>>> = {
  prepaid: {
    fixed: 'Charge automatically for a set number of sessions at the start of each billing cycle.',
    schedule: 'Charge automatically for the sessions on the calendar at the start of each billing cycle.',
    tuition: 'Charge a fixed amount at the start of each billing cycle, regardless of sessions.',
  },
  postpaid: {
    fixed: 'Charge automatically for a set number of sessions at the end of each billing cycle.',
    schedule: 'Charge automatically for the sessions on the calendar at the end of each billing cycle.',
    attendance: 'Automatically charge only for sessions marked as attended at the end of each billing cycle.',
    tuition: 'Charge a fixed amount at the end of each billing cycle, regardless of sessions.',
  },
  // The manual TIER has no kinds, so it has no per-kind description either.
  // Its copy lives in the "Compare plan types" modal; the tier radio itself
  // is just the word Manual.
  manual: {},
};

export const planTypeFor = (direction: PlanDirection, kind: PlanKind): PlanTypeKey => {
  // The manual tier maps to the manual plan type whatever kind it is paired
  // with: it carries no kinds, so nothing else can be meant by it.
  if (direction === 'manual') return 'manual';
  switch (kind) {
    case 'schedule':
      return direction === 'prepaid' ? 'prepaid-schedule' : 'postpaid-schedule';
    case 'attendance':
      return 'postpaid-metered';
    case 'manual':
      return 'manual';
    default: // fixed and tuition share the fixed types; tuition is "fixed with every quantity 0"
      return direction === 'prepaid' ? 'prepaid' : 'postpaid-fixed';
  }
};

/**
 * A kind shows when its underlying plan type is allowed; tuition follows fixed
 * (spec §5). WST-833: `visibleTypes` is
 * `visiblePlanTypes(currentProvider.allowed_plan_types, plan.plan_type)`.
 *
 * `kinds` is the CALLER's own allow-list, narrower than the clinic's: the
 * booking flow books slots against the plan it just created, and
 * `slots_to_book` reads the mirror `service_quantity` — which a Tuition plan
 * must carry as 0. Tuition is therefore never offered there. Undefined means
 * "every kind the clinic allows".
 */
export const visibleKinds = (
  direction: PlanDirection,
  visibleTypes: PlanTypeKey[],
  kinds?: PlanKind[]
): PlanKind[] =>
  KINDS_BY_DIRECTION[direction].filter(
    (kind) => (!kinds || kinds.includes(kind)) && visibleTypes.includes(planTypeFor(direction, kind))
  );

/**
 * The tiers the selector may offer, in display order. A direction shows when it
 * has at least one visible kind; the Manual TIER has no kinds to count, so it
 * shows whenever the `manual` plan type itself is on offer.
 */
export const visibleDirections = (visibleTypes: PlanTypeKey[], kinds?: PlanKind[]): PlanDirection[] =>
  (['prepaid', 'postpaid', 'manual'] as PlanDirection[]).filter((d) =>
    d === 'manual'
      ? (!kinds || kinds.includes('manual')) && visibleTypes.includes('manual')
      : visibleKinds(d, visibleTypes, kinds).length > 0
  );

export const isSelectionVisible = (
  direction: PlanDirection | undefined,
  kind: PlanKind | undefined,
  visibleTypes: PlanTypeKey[],
  kinds?: PlanKind[]
): boolean => {
  if (!direction || !kind) return false;
  // The manual tier is visible as a whole or not at all — visibleKinds would
  // answer "no kinds" for it and move a perfectly valid manual plan elsewhere.
  if (direction === 'manual') return kind === 'manual' && visibleDirections(visibleTypes, kinds).includes('manual');
  return visibleKinds(direction, visibleTypes, kinds).includes(kind);
};

/**
 * The first direction that has a visible kind, on that direction's recommended
 * kind. A create form no longer OPENS on this — nothing is preselected — but a
 * pair that stops being visible (the clinic's allow-list resolves after the
 * first render) still has to move somewhere the selector actually renders,
 * rather than leaving a lone "Postpaid" radio over an empty kind list.
 * Undefined when the allow-lists leave nothing visible at all.
 */
export const firstVisibleSelection = (
  visibleTypes: PlanTypeKey[],
  allowedKinds?: PlanKind[]
): { direction: PlanDirection; kind: PlanKind } | undefined => {
  for (const direction of visibleDirections(visibleTypes, allowedKinds)) {
    if (direction === 'manual') return { direction, kind: 'manual' };
    const kind = preferredKind(visibleKinds(direction, visibleTypes, allowedKinds));
    if (kind) return { direction, kind };
  }
  return undefined;
};

/** WST-681: a row whose service is a fee — billed as a flat line, never a session. */
export const isFeeRow = (r: Pick<ServiceRowValue, 'provider_service'>): boolean => isFeeService(r.provider_service);

/** WST-681: how a fee row bills (its override, else the service default); undefined for a session row. */
export const rowFeeFrequency = (
  r: Pick<ServiceRowValue, 'provider_service' | 'fee_frequency'> & { is_fee?: boolean }
): 'one_time' | 'recurring' | undefined =>
  (r.is_fee ?? isFeeRow(r))
    ? r.fee_frequency ?? r.provider_service?.effective_fee_frequency ?? r.provider_service?.fee_frequency ?? 'recurring'
    : undefined;

/**
 * WST-681: whether a plan bills nothing. Prefers the backend's `is_free` (false
 * while any fee row bills); an older payload falls back to the zero-rate check,
 * which a fee row with a rate still overrules.
 */
export const isFreePlan = (plan?: Partial<ClientSubscription>): boolean =>
  !!plan &&
  (plan.is_free ??
    ((!!plan.provider_service?.service?.is_free || plan.rate === 0) &&
      !(plan.services ?? []).some((r) => !!rowFeeFrequency(r) && (r.rate || 0) > 0)));

/** WST-681: every picked row is a one-time fee — nothing for the plan to bill each cycle. */
export const onlyOneTimeFees = (rows: Array<Pick<ServiceRowValue, 'provider_service' | 'fee_frequency'>>): boolean => {
  const picked = rows.filter((r) => r.provider_service);
  return picked.length > 0 && picked.every((r) => rowFeeFrequency(r) === 'one_time');
};

export const ONLY_ONE_TIME_FEES_MESSAGE =
  'A plan needs a session or a recurring fee. Bill a one-time fee on its own with Bill instead.';

/** WST-681: the rows that are sessions — every session count reads these. */
export const sessionRows = <T extends Pick<ServiceRowValue, 'provider_service'>>(rows: T[]): T[] =>
  rows.filter((r) => !isFeeRow(r));

/**
 * WST-681: the plan kinds that can hold these rows. The backend refuses fees on
 * By Attendance and Manual plans, and a fee-only plan must be a fixed charge.
 */
export const kindsForRows = (rows: ServiceRowValue[], allowed: PlanKind[]): PlanKind[] => {
  const picked = rows.filter((r) => r.provider_service);
  const fees = picked.filter(isFeeRow);
  if (fees.length === 0) return allowed;
  const feeOnly = fees.length === picked.length;
  return allowed.filter((k) => (feeOnly ? k === 'fixed' : k !== 'attendance' && k !== 'manual'));
};

const rowsOf = (plan: ClientSubscription): ClientSubscriptionService[] =>
  plan.services && plan.services.length > 0
    ? plan.services
    : plan.provider_service
    ? [{ uuid: null, provider_service: plan.provider_service, rate: plan.rate, payout: plan.payout,
         service_quantity: plan.service_quantity || 0, service_interval_count: plan.service_interval_count || 0 }]
    : [];

export const kindOf = (plan: ClientSubscription): PlanKind => {
  switch (plan.plan_type) {
    case 'prepaid-schedule':
    case 'postpaid-schedule':
      return 'schedule';
    case 'postpaid-metered':
      return 'attendance';
    case 'manual':
      return 'manual';
    default: {
      // WST-681: a fee-only plan also reads is_tuition (its mirror quantity is 0).
      if (plan.is_fee_only) return 'fixed';
      if (plan.is_tuition) return 'tuition';
      // Older API responses carry no flag: a fixed plan whose every row (or
      // legacy column) has a zero quantity prices the period (D1).
      const rows = rowsOf(plan);
      const quantities = rows.length ? rows.map((r) => r.service_quantity || 0) : [plan.service_quantity || 0];
      return quantities.every((q) => q === 0) ? 'tuition' : 'fixed';
    }
  }
};

export const directionOf = (plan: ClientSubscription): PlanDirection => {
  // Manual is its own tier, so a stored manual plan opens on it rather than on
  // Postpaid — where its kind radio no longer exists.
  if (plan.plan_type === 'manual') return 'manual';
  return plan.plan_type === 'prepaid' || plan.plan_type === 'prepaid-schedule' ? 'prepaid' : 'postpaid';
};

/** "Prepaid Tuition", "Postpaid By Schedule", … — the card / report label. */
export const planKindLabel = (plan: ClientSubscription): string => {
  if (!plan.plan_type) return '';
  if (kindOf(plan) === 'tuition') return `${directionOf(plan) === 'prepaid' ? 'Prepaid' : 'Postpaid'} Tuition`;
  return planTypes[plan.plan_type];
};

export const isPrepaidPlan = (plan: ClientSubscription): boolean => directionOf(plan) === 'prepaid';
export const isPerSessionKind = (kind?: PlanKind) => kind === 'schedule' || kind === 'attendance';

/**
 * D7: a pause can hand back credits only on a plan that prices sessions —
 * prepaid or postpaid-fixed, and never tuition, which prices the period.
 * `kindOf` carries the same is_tuition-or-zero-quantity fallback the plan
 * card uses, so a flag-less legacy tuition plan is still recognized here.
 */
export const pauseCreditsOffered = (plan: ClientSubscription): boolean =>
  (plan.plan_type === 'prepaid' || plan.plan_type === 'postpaid-fixed') && kindOf(plan) !== 'tuition' && !plan.is_fee_only;

export type CadenceOption = { label: string; value: BillingCadence };

// Plain wording only: the select's "(recommended)" hint is added by
// cadenceLabel, so the card can state a cadence without stripping it back off.
const BASE_CADENCES: CadenceOption[] = [
  { label: 'Every week', value: { interval: 'week', count: 1 } },
  { label: 'Every 2 weeks', value: { interval: 'week', count: 2 } },
  { label: 'Every 4 weeks', value: { interval: 'week', count: 4 } },
  { label: 'Every month', value: { interval: 'month', count: 1 } },
];
const DAILY: CadenceOption = { label: 'Every day', value: { interval: 'day', count: 1 } };

export const DEFAULT_CADENCE: BillingCadence = { interval: 'week', count: 2 };

/**
 * D5/D6. Fixed: a cadence is offered only when its count is a whole multiple of
 * every row's session interval count (no biweekly sessions on weekly billing),
 * and month only when every row is once-per-interval. Postpaid per-session
 * kinds add Every day. Tuition, manual, prepaid per-session: the base list.
 */
export const cadenceOptions = (args: {
  /** Undefined before the user has picked a plan type: the base list, as for tuition. */
  kind?: PlanKind;
  direction?: PlanDirection;
  rows: Array<
    Pick<ServiceRowValue, 'service_quantity' | 'service_interval_count'> &
      Partial<Pick<ServiceRowValue, 'provider_service' | 'fee_frequency'>>
  >;
  allowMonthlyBilling?: boolean;
  /**
   * The booking flow books slots against the plan it creates, and slot booking
   * only understands weekly session intervals — so it never offers Every day.
   */
  allowDailyBilling?: boolean;
  /** The cadence the plan is billed on today (edit mode) — always offered. */
  current?: BillingCadence;
}): CadenceOption[] => {
  const { kind, direction, rows, allowMonthlyBilling, allowDailyBilling, current } = args;
  let options = [...BASE_CADENCES];
  if (kind === 'fixed') {
    options = options.filter((o) => {
      if (o.value.interval === 'month') return rows.every((r) => (r.service_interval_count || 1) === 1);
      return rows.every((r) => o.value.count % (r.service_interval_count || 1) === 0);
    });
  }
  if (isPerSessionKind(kind) && direction === 'postpaid' && allowDailyBilling !== false) options = [...options, DAILY];
  if (allowMonthlyBilling === false) options = options.filter((o) => o.value.interval !== 'month');
  // Whatever the plan is billed on today stays selectable even when the rules
  // above never offer it — a legacy plan billed week/3, or a row on a 3-week
  // session interval. Otherwise opening the form would silently re-bill it.
  if (current && !options.some((o) => o.value.interval === current.interval && o.value.count === current.count)) {
    options = [...options, { label: cadenceLabelPlain(current), value: current }];
  }
  // WST-681: the backend refuses a recurring fee on a daily plan — not even the stored cadence survives one.
  if (rows.some((r) => rowFeeFrequency(r) === 'recurring')) options = options.filter((o) => o.value.interval !== 'day');
  // The select is where "(recommended)" belongs, so every label goes through
  // cadenceLabel on the way out.
  return options.map((o) => ({ ...o, label: cadenceLabel(o.value) }));
};

export const sameCadence = (a: BillingCadence, b: BillingCadence) => a.interval === b.interval && a.count === b.count;

/**
 * "Every 2 weeks" / "Every day" — the plain wording for a cadence, with no
 * "(recommended)" hint, for prose (the plan card) rather than a select. A
 * cadence no option offers (a legacy plan billed week/3) is worded generically.
 */
export const cadenceLabelPlain = (c: BillingCadence): string =>
  [...BASE_CADENCES, DAILY].find((o) => sameCadence(o.value, c))?.label ||
  `Every ${c.count > 1 ? `${c.count} ` : ''}${c.interval}${c.count > 1 ? 's' : ''}`;

/** cadenceLabelPlain plus the select's hint on the recommended cadence. */
export const cadenceLabel = (c: BillingCadence): string =>
  sameCadence(c, DEFAULT_CADENCE) ? `${cadenceLabelPlain(c)} (recommended)` : cadenceLabelPlain(c);

export type RowCadenceOption = { label: string; value: { service_quantity: number; service_interval_count: number } };

/** Per-row session cadence for FIXED rows, shaped by the billing interval. */
export const rowCadenceOptions = (billing: BillingCadence): RowCadenceOption[] => {
  if (billing.interval === 'month') {
    return [1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => ({
      label: n === 1 ? 'Once a month' : `${n} times a month`,
      value: { service_quantity: n, service_interval_count: 1 },
    }));
  }
  const weekly: RowCadenceOption[] = [
    { label: 'Once a Week', value: { service_quantity: 1, service_interval_count: 1 } },
    { label: 'Twice a Week', value: { service_quantity: 2, service_interval_count: 1 } },
    { label: 'Three times per Week', value: { service_quantity: 3, service_interval_count: 1 } },
    { label: 'Four times per Week', value: { service_quantity: 4, service_interval_count: 1 } },
    { label: 'Five times per Week', value: { service_quantity: 5, service_interval_count: 1 } },
  ];
  if (billing.count % 2 === 0) {
    weekly.push({ label: 'Biweekly (once every 2 weeks)', value: { service_quantity: 1, service_interval_count: 2 } });
  }
  return weekly;
};

// `??`, not `||`: a service priced at 0 legitimately prefills a 0 rate, and only
// a null/undefined rate should fall through to the parent service's price.
export const rateOf = (ps?: ProviderService): number | undefined =>
  ps?.rate ?? ps?.parent?.rate ?? undefined;

/**
 * The rate a freshly picked service puts on its row: the service's own price,
 * except on tuition (which prices the period) and across currencies — the old
 * form blanked the rate when the service was priced in another currency than
 * the plan, rather than charging 100 CAD as 100 USD.
 *
 * Once a service IS on the row the rate is floored at 0, as the old form's
 * `getValidNumber(…, 0)` floored it: an undefined rate fails validation, and in
 * the booking flow it leaves the Book button disabled (`subscription?.rate ===
 * undefined`) with nothing on screen to explain why — that flow's providers
 * often cannot see the rate cell at all. A row with no service stays rate-less.
 */
export const prefillRate = (ps?: ProviderService, kind: PlanKind = 'fixed', planCurrency?: string): number | undefined => {
  // WST-681: a fee keeps its own price even on tuition — it is not part of the period.
  if (!ps || (kind === 'tuition' && !isFeeService(ps))) return undefined;
  if (planCurrency && ps.currency && ps.currency !== planCurrency) return 0;
  return rateOf(ps) ?? 0;
};

export const emptyRow = (ps?: ProviderService, kind: PlanKind = 'fixed', planCurrency?: string): ServiceRowValue => ({
  provider_service: ps,
  rate: prefillRate(ps, kind, planCurrency),
  payout: undefined,
  service_quantity: !isFeeService(ps) && kind === 'fixed' ? 1 : 0,
  service_interval_count: 1,
  fee_frequency: undefined,
});

/**
 * The rows a kind can hold. Switching kind must not leave the previous kind's
 * shape behind: tuition prices the period (no row rates, no session counts),
 * the per-session kinds count nothing up front, and fixed needs at least one
 * session per interval and a rate to charge it at.
 */
export const reshapeRowsForKind = (rows: ServiceRowValue[], kind: PlanKind = 'fixed'): ServiceRowValue[] =>
  rows.map((row) => {
    // WST-681: a fee is priced on every kind and never counts sessions.
    if (isFeeRow(row)) {
      return { ...row, rate: row.rate ?? prefillRate(row.provider_service, 'fixed'), service_quantity: 0, service_interval_count: 1 };
    }
    if (kind === 'tuition') {
      return { ...row, rate: undefined, service_quantity: 0, service_interval_count: 1 };
    }
    if (kind === 'fixed') {
      return {
        ...row,
        // Same 0 floor as prefillRate: a fixed row carrying a service must
        // carry a rate, or the plan cannot be saved and says nothing about why.
        rate: row.rate ?? prefillRate(row.provider_service, 'fixed'),
        service_quantity: row.service_quantity || 1,
        service_interval_count: row.service_interval_count || 1,
      };
    }
    // schedule / attendance / manual: never billed on these counts — priced per
    // session and counted from the calendar, or issued by hand — but they are
    // NOT discarded. The booking flow seeds the form from the cadence its slots
    // were picked on, and AvailableTimeSlotViewSet.book refuses a booking whose
    // slot count differs from the plan's `slots_to_book` (= service_quantity):
    // zeroing here produced "0 != 1" on the very booking that created the plan.
    // A row that was never a fixed row starts at 0 (emptyRow) and stays there.
    return { ...row, service_quantity: row.service_quantity || 0, service_interval_count: row.service_interval_count || 1 };
  });

/**
 * Keep every fixed row on a session cadence the new billing cadence can
 * express: a biweekly row cannot survive weekly billing, and "8 times a month"
 * must not silently become "8 times a week". Returns the same array when
 * nothing had to move, so callers can skip a state update.
 */
export const renormalizeRowsForBilling = (rows: ServiceRowValue[], billing: BillingCadence): ServiceRowValue[] => {
  const options = rowCadenceOptions(billing);
  const fallback = options[0].value;
  let changed = false;
  const next = rows.map((row) => {
    if (isFeeRow(row)) return row; // WST-681: no session cadence to move
    const fits = options.some(
      (o) => o.value.service_quantity === row.service_quantity && o.value.service_interval_count === row.service_interval_count
    );
    if (fits) return row;
    changed = true;
    return { ...row, ...fallback };
  });
  return changed ? next : rows;
};

/** How many sessions a fixed plan's rows add up to over one billing cycle. */
export const sessionsPerCycle = (
  rows: Array<Pick<ServiceRowValue, 'service_quantity' | 'service_interval_count'>>,
  billing: BillingCadence
): number =>
  rows.reduce((total, row) => {
    const quantity = row.service_quantity || 0;
    if (billing.interval === 'month') return total + quantity;
    return total + (quantity * billing.count) / (row.service_interval_count || 1);
  }, 0);

/**
 * The sessions the backend's own itemize probe counts: row 0 alone, because
 * that is all `ClientSubscriptionSerializer` reads (client/serializers.py) and
 * all `subscription_quantity` mirrors (client/models.py). A later row billing
 * plenty cannot rescue a first row that bills half a session.
 */
export const itemizeSessionsPerCycle = (v: Pick<PlanFormValues, 'services' | 'billing'>): number =>
  sessionsPerCycle(sessionRows(v.services).slice(0, 1), v.billing);

/**
 * Whether this plan may itemize at all. Fixed only (a kind with no known
 * per-cycle session count has nothing to itemize against), and only once row 0
 * carries a whole session per billing cycle — the create pickers cannot produce
 * less, but edit mode always re-offers the stored cadence, so a legacy plan
 * billed weekly over a biweekly row reaches the form and would 400 ("this plan
 * bills 0.5") the moment the box was ticked.
 */
export const canItemize = (v: Pick<PlanFormValues, 'kind' | 'services' | 'billing'>): boolean =>
  v.kind === 'fixed' && itemizeSessionsPerCycle(v) >= 1;

/**
 * The end dates a plan may end on: cycle-boundary days only (the day before a
 * new cycle would start), 24 cycles ahead — what the old form offered. Only the
 * kinds that bill a known period have boundaries; schedule/attendance/manual
 * are counted from the calendar, so any day goes.
 */
export const allowedEndDates = (
  billing: BillingCadence,
  startDate: Date | undefined,
  kind?: PlanKind
): Date[] | undefined => {
  if (kind !== 'fixed' && kind !== 'tuition') return undefined;
  if (!startDate) return undefined;
  const dates: Date[] = [];
  let cursor = moment(startDate).startOf('day');
  for (let i = 0; i < 24; i++) {
    cursor = cursor.clone().add(billing.count, billing.interval as any);
    dates.push(cursor.clone().subtract(1, 'day').toDate());
  }
  return dates;
};

export type SettleOptions = {
  allowMonthlyBilling?: boolean;
  allowDailyBilling?: boolean;
  /** Edit mode: the cadence the plan is billed on today stays selectable. */
  isEdit?: boolean;
  /** Defaults to the values' own cadence — the stored one, on mount. */
  current?: BillingCadence;
  /**
   * Whether fixed rows may be moved onto a session cadence the billing cadence
   * can express. Defaults to create mode only: a stored plan whose row the
   * picker cannot express (6 sessions a week, a 3-week session interval) must
   * survive merely being opened, or saving an unrelated edit — a provider note
   * — silently rewrites the sessions the client is billed for. In edit mode the
   * caller passes true exactly when the user changed the billing cadence, which
   * is the one change that can strand a row.
   */
  renormalizeRows?: boolean;
};

/**
 * The consistency rules between kind, rows and cadence, as one pure step: a
 * cadence the rows can no longer be billed on falls back to the recommended
 * one, fixed rows are renormalized onto the cadence that survives (see
 * `renormalizeRows` — never on an edit-mode open), monthly billing clamps the
 * start date to the 28th, and only fixed plans itemize.
 *
 * The form runs this on every change AND once on the seed values — a seed the
 * rules reject (a monthly hint under allowMonthlyBilling={false}) would
 * otherwise show a blank cadence and still POST the monthly one.
 *
 * Returns the input untouched when nothing had to move, so callers can skip a
 * state update.
 */
export const settleValues = (values: PlanFormValues, opts: SettleOptions = {}): PlanFormValues => {
  const options = cadenceOptions({
    kind: values.kind,
    direction: values.direction,
    rows: values.services,
    allowMonthlyBilling: opts.allowMonthlyBilling,
    allowDailyBilling: opts.allowDailyBilling,
    current: opts.isEdit ? opts.current || values.billing : undefined,
  });
  let billing = values.billing;
  if (!options.some((o) => sameCadence(o.value, billing))) {
    billing = (options.find((o) => sameCadence(o.value, DEFAULT_CADENCE)) || options[0])?.value || billing;
  }
  const mayRenormalize = opts.renormalizeRows ?? !opts.isEdit;
  const services =
    values.kind === 'fixed' && mayRenormalize
      ? renormalizeRowsForBilling(values.services, billing)
      : values.services;
  let start_date = values.start_date;
  // The clamp rides the same guard as the rows: a STORED plan started on the
  // 30th must survive merely being opened. Clamping the seed made an edit-mode
  // start_date dirty on open, so a note-only save either moved the plan's start
  // date or was refused outright ("Start date can only be changed while the
  // plan is scheduled..."). It still applies the moment the user picks monthly
  // billing (which passes renormalizeRows), and on every create.
  if (mayRenormalize && billing.interval === 'month' && start_date && start_date.getDate() > 28) {
    start_date = moment(start_date).date(28).toDate();
  }
  const itemize_invoice = values.kind === 'fixed' ? values.itemize_invoice : false;
  if (
    billing === values.billing &&
    services === values.services &&
    start_date === values.start_date &&
    itemize_invoice === values.itemize_invoice
  ) {
    return values;
  }
  return { ...values, billing, services, start_date, itemize_invoice };
};

export const initialValues = (args: {
  plan?: ClientSubscription;
  provider?: Provider;
  client?: Client;
  currency?: string;
  providerService?: ProviderService;
  startDate?: Date;
  serviceInterval?: string; // booking flow hints (one-off is ignored)
  serviceIntervalCount?: number;
  serviceQuantity?: number;
}): PlanFormValues => {
  const { plan } = args;
  if (plan && plan.plan_type) {
    const kind = kindOf(plan);
    const rows = rowsOf(plan);
    return {
      // ClientSubscriptionSerializer does not serialize `provider`, so the
      // plan's own service is the only place the plan's provider survives a
      // round trip. Falling straight through to the caller's provider would
      // hand an edit dialog opened in clinic view the CLINIC — whose services
      // the backend then refuses ("does not belong to the plan's provider").
      provider: plan.provider || plan.provider_service?.provider || args.provider,
      client: plan.client || args.client,
      currency: plan.currency || args.currency || args.client?.currency,
      direction: directionOf(plan),
      kind,
      start_date: plan.start_date ? moment(plan.start_date).toDate() : args.startDate,
      end_date: plan.end_date
        ? moment(plan.end_date as any).toDate()
        : plan.plan_end_date
        ? moment(plan.plan_end_date, 'YYYY-MM-DD').toDate()
        : undefined,
      period_rate: kind === 'tuition' ? plan.rate : undefined,
      services: rows.map((r) => {
        // WST-681: a fee row keeps its rate on tuition and has no session cadence.
        const fee = isFeeService(r.provider_service);
        return {
          uuid: r.uuid ?? null,
          provider_service: r.provider_service,
          rate: kind === 'tuition' && !fee ? undefined : r.rate ?? undefined,
          // A null payout is kept as null (not undefined) so an edit that leaves
          // the row alone sends the server exactly what it already holds, and a
          // cleared payout is sent as an explicit null instead of being omitted.
          payout: r.payout,
          // Fixed rows need at least one session per interval; tuition and manual
          // count none. A per-session row keeps whatever the plan stores, so an
          // edit that re-sends the rows (a rate change) cannot zero the quantity
          // the book endpoint compares its slots against.
          service_quantity: fee
            ? 0
            : kind === 'fixed'
            ? r.service_quantity || 1
            : isPerSessionKind(kind)
            ? r.service_quantity || 0
            : 0,
          service_interval_count: fee || kind === 'tuition' || kind === 'manual' ? 1 : r.service_interval_count || 1,
          fee_frequency: r.fee_frequency ?? null,
        };
      }),
      billing: {
        interval: (plan.subscription_interval as BillingInterval) || 'week',
        count: plan.subscription_interval_count || 2,
      },
      itemize_invoice: !!plan.itemize_invoice,
      notify_on_payment: !!plan.notify_on_payment,
      note: plan.note,
      provider_note: plan.provider_note ?? undefined,
    };
  }
  const hintedWeekly = args.serviceInterval === 'week';
  const row = emptyRow(args.providerService, 'fixed', args.currency || args.client?.currency);
  if (hintedWeekly && args.serviceQuantity) {
    row.service_quantity = args.serviceQuantity;
    row.service_interval_count = args.serviceIntervalCount || 1;
  }
  return {
    provider: args.provider,
    client: args.client,
    currency: args.currency || args.client?.currency,
    // No plan type at all until the user picks one (WST-891 feedback). The row
    // above still takes the FIXED shape: it is the only shape a row can have
    // before there is a kind to reshape it for, and picking a kind reshapes it.
    direction: undefined,
    kind: undefined,
    start_date: args.startDate,
    end_date: undefined,
    period_rate: undefined,
    services: [row],
    billing: args.serviceInterval === 'month' ? { interval: 'month', count: 1 } : { ...DEFAULT_CADENCE },
    itemize_invoice: false,
    notify_on_payment: false,
    note: undefined,
    provider_note: undefined,
  };
};

/**
 * The start date may only move while the plan hasn't started; the backend
 * enforces this too (and rejects past dates). Lives here rather than in
 * planEdit.ts — which imports this module — so `buildEditPayload` can gate on
 * it without an import cycle; planEdit re-exports it for its own callers.
 */
export const canEditStartDate = (sub?: ClientSubscription): boolean =>
  // WST-950: status can still read Scheduled after billing started; has_started
  // is the backend's own rule.
  !!sub &&
  !sub.has_started &&
  (!sub.subscription_setup_complete || ['Scheduled', 'New'].includes(sub.status || ''));

const rowPayload = (r: ServiceRowValue, kind?: PlanKind) =>
  isFeeRow(r)
    ? {
        // WST-681: a fee bills one flat line on every kind — its own rate, no sessions.
        ...(r.uuid ? { uuid: r.uuid } : {}),
        provider_service_uuid: r.provider_service?.uuid,
        rate: r.rate,
        payout: r.payout ?? null,
        service_quantity: 0,
        service_interval_count: 1,
        fee_frequency: r.fee_frequency ?? null,
      }
    : sessionRowPayload(r, kind);

const sessionRowPayload = (r: ServiceRowValue, kind?: PlanKind) => ({
  ...(r.uuid ? { uuid: r.uuid } : {}),
  provider_service_uuid: r.provider_service?.uuid,
  rate: kind === 'tuition' ? null : r.rate,
  // Explicit null, never an omitted key: the serializer's `payout` is
  // required=False, so leaving it out would keep the payout already stored.
  payout: r.payout ?? null,
  // Only tuition forces the counts: it prices the PERIOD, and a non-zero
  // quantity is exactly what would make the backend read it as a fixed plan.
  // Every other kind sends the row's own counts — reshapeRowsForKind has
  // already put each kind's shape on the row, and a per-session row's quantity
  // is what `slots_to_book` (and so the book endpoint) reads.
  service_quantity: kind === 'tuition' ? 0 : r.service_quantity,
  service_interval_count: kind === 'tuition' ? 1 : r.service_interval_count,
});

/**
 * Mirror columns: what a pre-WST-891 reader (and the serializer's legacy path)
 * expects. On create these counts do not always survive as sent:
 * `createClientSubscription` still runs `normalizePlanIntervals`, which zeroes
 * service_interval_count/service_quantity on a month/month plan — the
 * serializer's `_mirror_fields_from_first_row` then refills them from row 0.
 */
const mirrorFields = (v: PlanFormValues) => {
  // WST-681: the backend sorts session rows first, so the mirror is the first
  // session row; a fee-only plan mirrors its fee row (always alongside `services`).
  const first = sessionRows(v.services)[0] ?? v.services[0];
  const tuition = v.kind === 'tuition';
  return {
    provider_service: first?.provider_service ? { uuid: first.provider_service.uuid } : undefined,
    rate: tuition ? v.period_rate : first?.rate,
    payout: first?.payout,
    service_interval: v.billing.interval,
    // As in rowPayload: only tuition forces the counts. `slots_to_book` reads
    // this mirror column, so a per-session plan created from the booking flow
    // has to mirror the quantity its slots were booked on.
    service_interval_count: tuition ? 1 : first?.service_interval_count ?? 1,
    service_quantity: tuition ? 0 : first?.service_quantity ?? (v.kind === 'fixed' ? 1 : 0),
    subscription_interval: v.billing.interval,
    subscription_interval_count: v.billing.count,
  };
};

/**
 * Loud rather than silent: a create form opens with no plan type at all, and a
 * payload built before one is picked would carry no `plan_type` and a Fixed
 * plan's mirror columns by accident. Every caller either gates on validity
 * (the form's own Invite button) or on the pair being set (the booking flow's
 * onChange), so reaching this is a bug, not a user state.
 */
export const buildCreatePayload = (v: PlanFormValues): Record<string, any> => {
  if (!v.direction || !v.kind) throw new Error('A service plan needs a plan type before it can be created');
  return buildCreatePayloadFor(v, v.direction, v.kind);
};

const buildCreatePayloadFor = (
  v: PlanFormValues,
  direction: PlanDirection,
  kind: PlanKind
): Record<string, any> => ({
  client: v.client,
  provider: v.provider,
  currency: v.currency,
  plan_type: planTypeFor(direction, kind),
  ...mirrorFields(v),
  services: v.services.map((r) => rowPayload(r, v.kind)),
  start_date: v.start_date ? moment(v.start_date).toISOString() : undefined,
  end_date: v.end_date ? moment(v.end_date).format('YYYY-MM-DD') : null,
  itemize_invoice: canItemize(v) ? !!v.itemize_invoice : false,
  notify_on_payment: !!v.notify_on_payment,
  note: v.note,
  provider_note: v.provider_note ?? undefined,
});

const sameRow = (a: ServiceRowValue, b: ServiceRowValue, kind?: PlanKind) => {
  const session = !isFeeRow(a); // WST-681: a fee is priced on every kind and has no cadence
  return (
    (a.uuid ?? null) === (b.uuid ?? null) &&
    a.provider_service?.uuid === b.provider_service?.uuid &&
    ((kind === 'tuition' && session) || (a.rate ?? null) === (b.rate ?? null)) &&
    (a.payout ?? null) === (b.payout ?? null) &&
    (a.fee_frequency ?? null) === (b.fee_frequency ?? null) &&
    (kind !== 'fixed' ||
      !session ||
      (a.service_quantity === b.service_quantity && a.service_interval_count === b.service_interval_count))
  );
};

/**
 * D10: the minimal PATCH body, or undefined when nothing changed. Always carries
 * uuid and provider_service (the backend requires it). Rows are sent as a whole
 * list whenever any row changed — the serializer treats `services` as
 * authoritative and matches rows by uuid.
 */
export const buildEditPayload = (plan: ClientSubscription, v: PlanFormValues): Record<string, any> | undefined => {
  const base = initialValues({ plan });
  const payload: Record<string, any> = {
    uuid: plan.uuid,
    provider_service: { uuid: (sessionRows(v.services)[0] ?? v.services[0])?.provider_service?.uuid },
  };
  let dirty = false;

  const rowsChanged =
    v.services.length !== base.services.length ||
    v.services.some((r, i) => !sameRow(r, base.services[i], v.kind));
  if (rowsChanged) {
    payload.services = v.services.map((r) => rowPayload(r, v.kind));
    dirty = true;
  }
  if (v.kind === 'tuition' && v.period_rate !== base.period_rate) {
    payload.rate = v.period_rate;
    // A top-level `rate` alone is not safe on a tuition plan: the serializer
    // overlays the legacy ROW_FIELDS (rate among them) onto row 0 and persists
    // it, leaving a tuition row carrying a rate — the shape _validate_plan_kind
    // rejects on the next rows-carrying write. Re-send the rows (which carry
    // rate: null) so row 0 is written from them instead.
    payload.services = v.services.map((r) => rowPayload(r, v.kind));
    dirty = true;
  }
  if (v.billing.interval !== base.billing.interval || v.billing.count !== base.billing.count) {
    payload.subscription_interval = v.billing.interval;
    payload.subscription_interval_count = v.billing.count;
    payload.service_interval = v.billing.interval; // the backend requires equal units
    dirty = true;
  }
  // A plan that has already started may not move its start date at all — the
  // backend 400s — so the key is never sent for one, not even when the field
  // itself somehow differs from what is stored.
  if (v.start_date && canEditStartDate(plan)) {
    const oldDay = base.start_date ? moment(base.start_date).format('YYYY-MM-DD') : undefined;
    const newDay = moment(v.start_date).format('YYYY-MM-DD');
    if (newDay !== oldDay) {
      payload.start_date = moment(v.start_date).toISOString();
      dirty = true;
    }
  }
  // A plan the backend's probe would refuse never sends the flag at all: not
  // the `true` that 400s, and not a `false` that would silently switch off a
  // legacy plan nobody asked to change.
  // WST-681: the exception is a plan edited down to fee rows only — it cannot
  // itemize, and the backend refuses it while the stored flag stays on.
  const feeOnly = v.services.length > 0 && sessionRows(v.services).length === 0;
  const itemize = canItemize(v) && !!v.itemize_invoice;
  if ((canItemize(v) || feeOnly) && itemize !== base.itemize_invoice) {
    payload.itemize_invoice = itemize;
    dirty = true;
  }
  if (!!v.notify_on_payment !== base.notify_on_payment) {
    payload.notify_on_payment = !!v.notify_on_payment;
    dirty = true;
  }
  if ((v.provider_note || '') !== (base.provider_note || '')) {
    payload.provider_note = v.provider_note || '';
    dirty = true;
  }
  return dirty ? payload : undefined;
};

export const servicePlanSchema = Yup.object().shape({
  start_date: Yup.date().required('Start date is required'),
  end_date: Yup.date()
    .nullable()
    .notRequired()
    .when('start_date', (startDate: any, schema: any) =>
      startDate ? schema.min(startDate, 'End date must be on or after start date') : schema
    ),
  kind: Yup.string().required(),
  direction: Yup.string().required(),
  period_rate: Yup.number().when('kind', {
    is: 'tuition',
    then: (s: any) => s.required('A tuition plan needs the rate per period').moreThan(0, 'The period rate must be above 0'),
    otherwise: (s: any) => s.notRequired(),
  }),
  services: Yup.array()
    .of(
      Yup.object().shape({
        provider_service: Yup.object().required('Select a service'),
        // WST-681: a fee row always needs its rate, and never a session count.
        rate: Yup.number().when(['provider_service', '$kind'], (ps: any, kind: any, s?: any) =>
          !isFeeService(ps) && (kind === 'tuition' || kind === 'manual')
            ? s.notRequired()
            : s.required('A rate is required').min(0)
        ),
        service_quantity: Yup.number().when(['provider_service', '$kind'], (ps: any, kind: any, s?: any) =>
          kind === 'fixed' && !isFeeService(ps)
            ? s.min(1, 'Every service on a fixed plan needs at least one session per interval')
            : s.min(0)
        ),
        service_interval_count: Yup.number().min(0),
      })
    )
    .min(1, 'A service plan needs at least one service')
    .test('no-duplicate-service', 'A service is listed twice', (rows: any) => {
      const ids = (rows || []).map((r: any) => r?.provider_service?.uuid).filter(Boolean);
      return new Set(ids).size === ids.length;
    })
    .test('needs-session-or-recurring', ONLY_ONE_TIME_FEES_MESSAGE, (rows: any) => !onlyOneTimeFees(rows || [])),
});

/**
 * The per-row rate rule reads the plan kind from the Yup *context* (`$kind`) —
 * a row has no `kind` sibling of its own — and formik's `validationSchema`
 * never forwards a context, so the form validates through this function
 * instead. Resolves to formik's nested error object ({} when the plan is
 * valid).
 */
export const validateServicePlan = (values: PlanFormValues): Promise<Record<string, any>> =>
  validateYupSchema(values, servicePlanSchema, false, { kind: values.kind })
    .then(() => ({}))
    .catch((err: any) => {
      // Mirrors formik's own runValidationSchema: only a Yup ValidationError is
      // form state; anything else is a real bug and must not read as "valid".
      if (err.name !== 'ValidationError') throw err;
      return yupToFormErrors(err);
    });

/**
 * Enforce the backend invariant on outgoing create payloads: the billing
 * interval unit (subscription_interval) must match the service interval unit
 * (service_interval), or the backend 400s — and historically wrote poisoned
 * rows. Never mutates the input (pass-through cases return it unchanged) and
 * touches only the five interval fields. Divergent *counts* on matching 'week' units are
 * valid (e.g. service week/1/1 billed week/2) and are left alone; 'month'
 * plans are canonicalized to service 0/0 with billing count 1.
 */
export const normalizePlanIntervals = <T extends Partial<ClientSubscription>>(payload: T): T => {
  const svc = payload.service_interval;
  const bill = payload.subscription_interval;
  const billCount = payload.subscription_interval_count;

  // 'one-off' is a frontend-only sentinel for one-off bookings, never a real plan.
  if (svc === 'one-off' || bill === 'one-off') return payload;
  // Both unset: the backend defaults both to 'week' — already consistent.
  if (svc === undefined && bill === undefined) return payload;

  const out = { ...payload };

  if (svc === undefined) {
    // Only the billing interval is set: derive the service interval from it.
    out.service_interval = bill;
    if (bill === 'month') {
      out.service_interval_count = 0;
      out.service_quantity = 0;
      out.subscription_interval_count = 1;
    }
    return out;
  }

  if (bill === undefined) {
    // Only the service interval is set: derive the billing interval from it.
    out.subscription_interval = svc;
    out.subscription_interval_count = svc === 'month' ? 1 : 2;
    return out;
  }

  if (svc === bill) {
    if (svc === 'month') {
      // Canonical monthly shape: service counts 0/0, billed every 1 month.
      out.subscription_interval_count = 1;
      out.service_interval_count = 0;
      out.service_quantity = 0;
    }
    // Matching 'week' units with divergent counts are valid — leave untouched.
    return out;
  }

  // Units mismatch: the service interval wins. The count formula mirrors the
  // billing pair buildEditPayload above sends — keep the two consistent.
  out.subscription_interval = svc;
  out.subscription_interval_count =
    svc === 'month' ? 1 : bill === 'week' && billCount ? billCount : 2;
  if (svc === 'month') {
    out.service_interval_count = 0;
    out.service_quantity = 0;
  }
  return out;
};
